// src/components/devices/DeviceAdder.js
import React, { useContext, useState } from 'react';
import { toast } from '../../lib/toast';
import { X } from 'lucide-react';
import { AppContext } from '../../context/AppContext';
import ZigbeeDeviceForm from './deviceForms/ZigbeeDeviceForm';
import { useAuth } from '../../context/AuthContext';

const DeviceAdder = ({ onClose, onDeviceAdded }) => {
  const { refreshDevices } = useContext(AppContext);

  const [deviceType] = useState('zigbee');
  const [isAdding, setIsAdding] = useState(false);

  const { apiRequest } = useAuth();
  const API_BASE_URL = '';
  const API_PATH = '/api';

  const handleAddZigbeeDevice = async (zigbeeData) => {
    try {
      setIsAdding(true);

      const { response, data } = await apiRequest(
        `${API_BASE_URL}${API_PATH}/devices/zigbee/discovery/enable`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            timeout: zigbeeData.discoveryTimeout || 600,
          }),
        }
      );

      if (!response.ok) {
        throw new Error(data.message || 'Failed to enable Zigbee discovery mode');
      }

      toast.success(
        `Zigbee discovery enabled for ${Math.round(
          (zigbeeData.discoveryTimeout || 600) / 60
        )} minutes`
      );

      await refreshDevices();

      if (onDeviceAdded) onDeviceAdded();
      onClose();
    } catch (error) {
      console.error('Error enabling Zigbee discovery:', error);
      toast.error(`Failed to enable Zigbee discovery: ${error.message}`);
    } finally {
      setIsAdding(false);
    }
  };

  const renderDeviceForm = () => {
    if (deviceType === 'zigbee') {
      return (
        <ZigbeeDeviceForm
          onSubmit={handleAddZigbeeDevice}
          onCancel={onClose}
          isAdding={isAdding}
        />
      );
    }
    return null;
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 md:items-center"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !isAdding) onClose();
      }}
    >
      <div className="w-full md:max-w-md">
        <div className="mx-auto mb-3 hidden h-1.5 w-12 rounded-full bg-white/30 md:block" />

        <div className="w-full max-h-[85dvh] flex flex-col rounded-t-3xl md:rounded-3xl border border-border/60 bg-background/80 backdrop-blur-xl shadow-2xl overflow-hidden">
          {/* Grab handle */}
          <div className="flex justify-center pt-3">
            <div className="h-1.5 w-10 rounded-full bg-muted-foreground/25" />
          </div>

          {/* Header */}
          <div className="px-5 pt-3 pb-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="text-base font-semibold tracking-tight">
                  Add Zigbee Device
                </h3>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  Enable discovery and put your device into pairing mode.
                </p>
              </div>

              <button
                onClick={onClose}
                disabled={isAdding}
                className="h-9 w-9 rounded-full flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted/60 transition disabled:opacity-50"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="px-5 pb-5 flex-1 min-h-0 overflow-y-auto">
            {renderDeviceForm()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeviceAdder;
